import React, { useEffect, useMemo, useState } from 'react';
import { ClinicTouchCanvas } from './ClinicTouchCanvas';
import { ClinicProcedureTemplateCanvas } from './ClinicProcedureTemplateCanvas';

const makeFutureLocalDateTime = (days) => {
  const target = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const pad = (value) => String(value).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
};

const initialForm = {
  appointment_id: '',
  patient_name: '',
  patient_phone: '',
  patient_email: '',
  chief_complaint: '',
  assessment: '',
  treatment_plan: '',
  nurse_notes: '',
  post_treatment_notes: '',
  follow_up_date: '',
  canvas_image_data_url: '',
  signature_data_url: '',
  template_state: { version: 1, pages: {} },
  status: 'cerrado',
};

export const ClinicQuickConsultPanel = ({ workspace, onSubmitConsultation, saving }) => {
  const [form, setForm] = useState(initialForm);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [prescribedItems, setPrescribedItems] = useState([]);

  const todayAppointments = useMemo(
    () => (workspace.appointments || []).filter((appointment) => appointment.is_today || appointment.is_upcoming).slice(0, 8),
    [workspace.appointments],
  );

  const patientCards = useMemo(() => workspace.patient_crm || [], [workspace.patient_crm]);
  const inventoryMap = useMemo(() => new Map((workspace.inventory_items || []).map((item) => [item.id, item])), [workspace.inventory_items]);

  useEffect(() => {
    if (!form.follow_up_date) {
      setForm((prev) => ({ ...prev, follow_up_date: makeFutureLocalDateTime(7) }));
    }
  }, [form.follow_up_date]);

  const hydrateFromAppointment = (appointmentId) => {
    const appointment = todayAppointments.find((item) => item.id === appointmentId);
    if (!appointment) return;
    setForm((prev) => ({
      ...prev,
      appointment_id: appointment.id,
      patient_name: appointment.patient_name || '',
      patient_phone: appointment.patient_phone || '',
      patient_email: appointment.patient_email || '',
      chief_complaint: prev.chief_complaint || `Consulta ${appointment.appointment_type || 'dermatológica'}`,
    }));
  };

  const hydrateFromPatient = (patientKey) => {
    const patient = patientCards.find((item) => item.patient_key === patientKey);
    if (!patient) return;
    setForm((prev) => ({
      ...prev,
      patient_name: patient.patient_name || '',
      patient_phone: patient.patient_phone || '',
      patient_email: patient.patient_email || '',
      chief_complaint: prev.chief_complaint || patient.chief_complaint || '',
      treatment_plan: prev.treatment_plan || patient.treatment_plan || '',
      follow_up_date: patient.follow_up_date || prev.follow_up_date,
    }));
  };

  const addProduct = () => {
    const product = inventoryMap.get(selectedProductId);
    if (!product) return;
    setPrescribedItems((prev) => [...prev, { inventory_item_id: product.id, product_name: product.name, quantity: selectedQuantity }]);
    setSelectedProductId('');
    setSelectedQuantity(1);
  };

  const removeProduct = (index) => {
    setPrescribedItems((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const submit = async (event) => {
    event.preventDefault();
    const success = await onSubmitConsultation({
      ...form,
      prescribed_items: prescribedItems,
      template_state: form.template_state,
      template_label: workspace.clinical_master_template?.name,
      template_source_url: workspace.clinical_master_template?.pdf_url,
      template_page_count: workspace.clinical_master_template?.page_count,
    });
    if (success) {
      setForm(initialForm);
      setPrescribedItems([]);
    }
  };

  return (
    <section className="space-y-5" data-testid="clinic-quick-consult-panel">
      <article className="rounded-[32px] border border-white/60 bg-[#102a43] p-5 text-white shadow-[0_24px_60px_rgba(16,42,67,0.28)] sm:p-6" data-testid="clinic-quick-consult-hero">
        <p className="text-xs uppercase tracking-[0.24em] text-cyan-200">Consulta en 90 segundos</p>
        <h2 className="mt-2 text-3xl font-black">Tablet-first. Sin pantallas de sobra.</h2>
        <p className="mt-3 max-w-3xl text-sm text-cyan-50" data-testid="clinic-quick-consult-message">{workspace.quick_consult?.tablet_message}</p>
        <div className="mt-5 grid gap-3 md:grid-cols-4">
          {['Paciente', 'Valoración', 'Dibujo + firma', 'Prescripción + cierre'].map((step, index) => (
            <div key={step} className="rounded-[22px] bg-white/10 px-4 py-3" data-testid={`clinic-quick-consult-step-${index + 1}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">Paso {index + 1}</p>
              <p className="mt-2 text-sm font-semibold text-white">{step}</p>
            </div>
          ))}
        </div>
      </article>

      <form onSubmit={submit} className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]" data-testid="clinic-quick-consult-form">
        <div className="space-y-5">
          <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6" data-testid="clinic-quick-consult-patient-card">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">1. Elegir paciente</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="clinic-quick-consult-appointments-list">
              {todayAppointments.length ? todayAppointments.map((appointment) => (
                <button
                  key={appointment.id}
                  type="button"
                  onClick={() => hydrateFromAppointment(appointment.id)}
                  className={`rounded-[22px] border px-4 py-4 text-left ${form.appointment_id === appointment.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-[#f8fafc] text-slate-700'}`}
                  data-testid={`clinic-quick-consult-appointment-${appointment.id}`}
                >
                  <p className="text-sm font-semibold">{appointment.patient_name}</p>
                  <p className="mt-1 text-xs">{appointment.appointment_type} · {new Date(appointment.scheduled_at).toLocaleString('es-ES')}</p>
                </button>
              )) : <div className="rounded-[22px] border border-dashed border-slate-300 bg-[#f8fafc] px-4 py-4 text-sm text-slate-500" data-testid="clinic-quick-consult-no-appointments">No hay citas de hoy; puedes abrir una consulta manual.</div>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="clinic-quick-consult-patient-shortcuts">
              {patientCards.slice(0, 6).map((patient) => (
                <button
                  key={patient.patient_key}
                  type="button"
                  onClick={() => hydrateFromPatient(patient.patient_key)}
                  className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700"
                  data-testid={`clinic-quick-consult-patient-${patient.patient_key}`}
                >
                  {patient.patient_name}
                </button>
              ))}
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input data-testid="clinic-quick-consult-patient-name" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Paciente" value={form.patient_name} onChange={(event) => setForm((prev) => ({ ...prev, patient_name: event.target.value }))} required />
              <input data-testid="clinic-quick-consult-patient-phone" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Teléfono" value={form.patient_phone} onChange={(event) => setForm((prev) => ({ ...prev, patient_phone: event.target.value }))} />
              <input data-testid="clinic-quick-consult-patient-email" type="email" className="sm:col-span-2 rounded-2xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.patient_email} onChange={(event) => setForm((prev) => ({ ...prev, patient_email: event.target.value }))} />
            </div>
          </article>

          <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6" data-testid="clinic-quick-consult-assessment-card">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">2. Valoración</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="clinic-quick-consult-chief-presets">
              {workspace.quick_consult?.chief_complaint_presets?.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, chief_complaint: preset }))}
                  className="rounded-full bg-[#e0f2fe] px-4 py-2 text-sm font-semibold text-[#075985]"
                  data-testid={`clinic-quick-consult-chief-preset-${preset.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              <textarea data-testid="clinic-quick-consult-chief-input" className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Motivo de consulta" value={form.chief_complaint} onChange={(event) => setForm((prev) => ({ ...prev, chief_complaint: event.target.value }))} required />
              <textarea data-testid="clinic-quick-consult-assessment-input" className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Valoración clínica" value={form.assessment} onChange={(event) => setForm((prev) => ({ ...prev, assessment: event.target.value }))} required />
              <textarea data-testid="clinic-quick-consult-treatment-input" className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Plan terapéutico" value={form.treatment_plan} onChange={(event) => setForm((prev) => ({ ...prev, treatment_plan: event.target.value }))} required />
              <textarea data-testid="clinic-quick-consult-post-treatment-input" className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Seguimiento / cuidados post-tratamiento" value={form.post_treatment_notes} onChange={(event) => setForm((prev) => ({ ...prev, post_treatment_notes: event.target.value }))} />
            </div>
          </article>
        </div>

        <div className="space-y-5">
          <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6" data-testid="clinic-quick-consult-canvas-card">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">3. Dibujo + firma</p>
            <div className="mt-4 space-y-4">
              <ClinicProcedureTemplateCanvas
                template={workspace.clinical_master_template}
                value={form.canvas_image_data_url}
                stateValue={form.template_state}
                onChange={(canvasValue) => setForm((prev) => ({ ...prev, canvas_image_data_url: canvasValue }))}
                onStateChange={(templateState) => setForm((prev) => ({ ...prev, template_state: templateState }))}
                testId="clinic-quick-consult-canvas"
                clearTestId="clinic-quick-consult-canvas-clear"
              />
              <ClinicTouchCanvas
                label="Firma rápida"
                helper="Confirma la consulta en la misma pantalla."
                value={form.signature_data_url}
                onChange={(signatureValue) => setForm((prev) => ({ ...prev, signature_data_url: signatureValue }))}
                testId="clinic-quick-consult-signature"
                clearTestId="clinic-quick-consult-signature-clear"
                variant="signature"
              />
            </div>
          </article>

          <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6" data-testid="clinic-quick-consult-prescription-card">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">4. Prescripción + cierre</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_110px_auto]">
              <select data-testid="clinic-quick-consult-product-select" className="rounded-2xl border border-slate-200 px-4 py-3" value={selectedProductId} onChange={(event) => setSelectedProductId(event.target.value)}>
                <option value="">Producto de inventario</option>
                {workspace.inventory_items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <input data-testid="clinic-quick-consult-quantity-input" type="number" min="1" className="rounded-2xl border border-slate-200 px-4 py-3" value={selectedQuantity} onChange={(event) => setSelectedQuantity(Number(event.target.value))} />
              <button type="button" onClick={addProduct} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white" data-testid="clinic-quick-consult-add-product">Añadir</button>
            </div>
            <div className="mt-4 space-y-2" data-testid="clinic-quick-consult-prescribed-list">
              {prescribedItems.length ? prescribedItems.map((item, index) => (
                <div key={`${item.inventory_item_id}-${index}`} className="flex items-center justify-between rounded-2xl bg-[#f8fafc] px-4 py-3 text-sm text-slate-700" data-testid={`clinic-quick-consult-prescribed-item-${index}`}>
                  <span>{item.product_name}</span>
                  <div className="flex items-center gap-3">
                    <span>{item.quantity} uds</span>
                    <button type="button" onClick={() => removeProduct(index)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600" data-testid={`clinic-quick-consult-remove-product-${index}`}>Quitar</button>
                  </div>
                </div>
              )) : <p className="text-sm text-slate-500" data-testid="clinic-quick-consult-prescribed-empty">Añade productos para descontar stock al cerrar la consulta.</p>}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-400">Seguimiento</label>
                <input data-testid="clinic-quick-consult-follow-up-input" type="datetime-local" className="w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.follow_up_date} onChange={(event) => setForm((prev) => ({ ...prev, follow_up_date: event.target.value }))} />
                <div className="mt-2 flex flex-wrap gap-2" data-testid="clinic-quick-consult-follow-up-presets">
                  {workspace.quick_consult?.follow_up_presets?.map((days) => (
                    <button type="button" key={days} onClick={() => setForm((prev) => ({ ...prev, follow_up_date: makeFutureLocalDateTime(days) }))} className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600" data-testid={`clinic-quick-consult-follow-up-${days}d`}>
                      +{days} días
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-400">Cierre</label>
                <select data-testid="clinic-quick-consult-status-select" className="w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}>
                  {workspace.quick_consult?.close_statuses?.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
                <textarea data-testid="clinic-quick-consult-nurse-notes" className="mt-3 min-h-[92px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Notas rápidas de cierre" value={form.nurse_notes} onChange={(event) => setForm((prev) => ({ ...prev, nurse_notes: event.target.value }))} />
              </div>
            </div>
          </article>
        </div>

        <div className="xl:col-span-2">
          <div className="sticky bottom-4 rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-[0_16px_50px_rgba(15,23,42,0.15)] sm:p-5" data-testid="clinic-quick-consult-sticky-bar">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Consulta rápida preparada para cerrar</p>
                <p className="mt-1 text-xs text-slate-500">Si elegiste una cita, al guardar también la marcaré como completada.</p>
              </div>
              <button type="submit" className="rounded-full bg-slate-900 px-6 py-4 text-base font-semibold text-white transition hover:bg-slate-700" data-testid="clinic-quick-consult-submit" disabled={saving === 'quick-consult-create'}>
                {saving === 'quick-consult-create' ? 'Cerrando consulta…' : 'Guardar y cerrar consulta'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};